import { z } from 'zod';
import { PASSWORD_MAX_LENGTH, PERMISSIONS, ROLES } from '@bekebi/domain';
import { uuidSchema } from '../common';

/**
 * The signed-in user's session (one login for the whole Bekebi Portal). The Bekebi User ID is
 * shown for identification only; it is never a credential. No Payment Account data appears here.
 */
export const sessionResponseSchema = z.object({
  user: z.object({
    id: uuidSchema,
    /** Bekebi User ID, e.g. BEKEBI-000001. */
    bekebiUserId: z.string(),
    email: z.string(),
    role: z.enum(ROLES),
    /** What this user may do; the server enforces the same list. */
    permissions: z.array(z.enum(PERMISSIONS)),
  }),
  session: z.object({ expiresAt: z.string() }),
});
export type SessionResponse = z.infer<typeof sessionResponseSchema>;

// ---------------------------------------------------------------------------
// One-time account links and passwords (DECISIONS.md A-AC1)
// ---------------------------------------------------------------------------

/** A link token: 256 random bits, base64url (43 characters). Sent in a body, never in a URL. */
export const accountLinkTokenSchema = z.string().regex(/^[A-Za-z0-9_-]{43}$/);

export const accountLinkInspectRequestSchema = z.strictObject({ token: accountLinkTokenSchema });

/** What the set-password page shows for an open link. A closed link gets one fixed 410 instead. */
export const accountLinkInspectResultSchema = z.object({
  purpose: z.enum(['invitation', 'password_reset']),
  email: z.string(),
  expiresAt: z.string(),
});
export type AccountLinkInspectResult = z.infer<typeof accountLinkInspectResultSchema>;

export const accountLinkCompleteRequestSchema = z.strictObject({
  token: accountLinkTokenSchema,
  /** Checked against the password policy server-side, hashed (Argon2id), never stored in clear. */
  password: z.string().min(1).max(PASSWORD_MAX_LENGTH),
});
export type AccountLinkCompleteRequest = z.infer<typeof accountLinkCompleteRequestSchema>;

/** "Forgot password?": always answered the same way, whether or not the account exists. */
export const passwordResetRequestSchema = z.strictObject({
  email: z.string().trim().min(1).max(254),
});
export type PasswordResetRequest = z.infer<typeof passwordResetRequestSchema>;

/** A signed-in user changing their own password. */
export const passwordChangeRequestSchema = z.strictObject({
  currentPassword: z
    .string()
    .min(1)
    .max(PASSWORD_MAX_LENGTH * 4),
  newPassword: z.string().min(1).max(PASSWORD_MAX_LENGTH),
});
export type PasswordChangeRequest = z.infer<typeof passwordChangeRequestSchema>;
